import React, { useEffect, useMemo, useState } from "react";
import { Form, Checkbox, Radio, Row, Col, Input, Tooltip, InputNumber } from "antd";
import sumBy from "lodash/sumBy";

import "./SelectPack.scss";
import { convertArrayToObject } from "helper/util";

const SelectPack = ({
  packages,
  formSelectPack,
  dropId,
  dropoffAddress, // NEW: Dropoff address for header
  pickups, // NEW: Full pickups array for grouping
  dropoffs, // NEW: Full dropoffs array for showing allocation locations
  onOk,
  packageTypeList
}) => {
  const objPackageTypeList = useMemo(
    () => convertArrayToObject(packageTypeList, "id"),
    [packageTypeList]
  );
  const [packsForm, setPacksForm] = useState({});
  const [checkPacks, setCheckPacks] = useState([]);

  useEffect(() => {
    formSelectPack.resetFields();
    const rs = {};
    const dataForm = {};
    const rsQuantity = [];
    const mapByKeys = (packs) => {
      const rsMapKeys = {};
      packs.forEach((pack) => {
        const packageType = objPackageTypeList[pack.packageTypeId];
        rsMapKeys[packageType.name] = rsMapKeys[packageType.name] || [];
        rsMapKeys[packageType.name].push(pack);
      });
      return rsMapKeys;
    };
    const mapByQuantity = (array) => {
      return array
        .map((element) => {
          if (!element.dropoffs || !element.dropoffs.length) {
            // Package not yet assigned to ANY dropoff
            return {
              id: element.id,
              name: element.name,
              quantity: element.quantity,
              quantityInput: 0,
              ischeck: false,
              barcode: element.barcode || "",
              allocatedDropoffs: [], // NEW: No allocations
              isFullyAllocated: false, // NEW: Can be allocated
              isDisabled: false, // NEW: Enable checkbox
              pickupId: element.pickupId // NEW: Track pickup for grouping
            };
          }

          // Build list of allocations to OTHER dropoffs
          const allocatedDropoffs = element.dropoffs
            .filter((drop) => dropId !== drop.id)
            .map((drop) => ({
              dropoffId: drop.id,
              quantity: drop.quantity
            }));

          const quantityOther = sumBy(
            element.dropoffs.filter((drop) => dropId !== drop.id),
            "quantity"
          );

          const currentDropoff = element.dropoffs.find((drop) => dropId === drop.id);
          const quantityCurrent = currentDropoff?.quantity || 0;
          const quantityAvailable = element.quantity - quantityOther;
          const isFullyAllocated = quantityAvailable <= 0;

          // NEW: Return ALL packages, even fully allocated ones
          return {
            id: element.id,
            name: element.name,
            quantity: Math.max(quantityAvailable, 0), // Available quantity (0 if fully allocated)
            quantityInput: quantityCurrent,
            isCheck: quantityCurrent > 0,
            barcode: currentDropoff?.barcode || element.barcode || "",
            allocatedDropoffs: allocatedDropoffs, // NEW: Array of {dropoffId, quantity}
            isFullyAllocated: isFullyAllocated, // NEW: True if quantity - quantityOther <= 0
            isDisabled: isFullyAllocated, // NEW: Disable checkbox if fully allocated
            totalQuantity: element.quantity, // NEW: For display purposes (x / totalQuantity)
            pickupId: element.pickupId // NEW: Track pickup for grouping
          };
        })
        .filter(Boolean); // Filter out null values (keep all for now)
    };
    console.log("packages", packages);
    console.log(
      "packages with barcodes:",
      packages.map((p) => ({ id: p.id, name: p.name, barcode: p.barcode }))
    );
    const packByKeys = mapByKeys(packages);
    const packsByQuantity = mapByQuantity(packages); // Get all packages with metadata

    // NEW: Group packages by pickup location
    const packsByPickup = {};
    packsByQuantity.forEach((pack) => {
      const pId = pack.pickupId || "unknown";
      if (!packsByPickup[pId]) {
        packsByPickup[pId] = [];
      }
      packsByPickup[pId].push(pack);
    });

    // Build rsQuantity with pickup info and create the form structure
    Object.keys(packsByPickup).forEach((pId) => {
      packsByPickup[pId].forEach((pack) => {
        rsQuantity.push({
          id: pack.id,
          quantityInput: pack.quantityInput,
          quantity: pack.quantity,
          quantityLeft: pack.quantity - pack.quantityInput,
          isCheck: pack.isCheck,
          barcode: pack.barcode || "",
          allocatedDropoffs: pack.allocatedDropoffs || [],
          isFullyAllocated: pack.isFullyAllocated || false,
          isDisabled: pack.isDisabled || false,
          totalQuantity: pack.totalQuantity || pack.quantity,
          pickupId: pack.pickupId
        });
        dataForm[`${pack.id}+${dropId}`] = pack.quantityInput;
        dataForm[`barcode_${pack.id}+${dropId}`] = pack.barcode || "";
        console.log(`Setting barcode for ${pack.id}: ${pack.barcode}`);
      });
    });

    // Keep original rs structure for backward compatibility but also store pickup grouping
    Object.keys(packByKeys).forEach((k) => {
      rs[k] = [];
      const packsByQuantityForType = mapByQuantity(packByKeys[k]);
      packsByQuantityForType.forEach((packByQuantity) => {
        rs[k].push({
          id: packByQuantity.id,
          name: packByQuantity.name,
          pickupId: packByQuantity.pickupId
        });
      });
    });

    setPacksForm(rs);
    setCheckPacks(rsQuantity);
    formSelectPack.setFieldsValue(dataForm);
    // eslint-disable-next-line
  }, [dropId, formSelectPack, packages]);

  const onChangeInput = (changedValues, allValues) => {
    const value = Object.values(changedValues)[0];
    const newCheckPacks = [...checkPacks];
    const index = newCheckPacks.findIndex(
      (newP) => newP.id === Object.keys(changedValues)[0].split("+")[0]
    );
    newCheckPacks[index].quantityInput = Number(value);
    newCheckPacks[index].quantityLeft = newCheckPacks[index].quantity - Number(value);
    setCheckPacks(newCheckPacks);
  };

  const onChangeCheck = (e, packId) => {
    const newCheckPacks = [...checkPacks];
    const index = newCheckPacks.findIndex((newP) => newP.id === packId);

    // NEW: Prevent checking disabled packages (fully allocated)
    if (newCheckPacks[index].isDisabled && e.target.checked) {
      e.preventDefault();
      return;
    }

    newCheckPacks[index].isCheck = e.target.checked;
    if (e.target.checked) {
      // NEW: Default to 1 when checking
      newCheckPacks[index].quantityInput = 1;
      const dataForm = {};
      dataForm[`${newCheckPacks[index].id}+${dropId}`] = 1;
      formSelectPack.setFieldsValue(dataForm);
    } else {
      // Unchecking - clear the quantity
      const quantityInput = newCheckPacks[index].quantityInput;
      newCheckPacks[index].quantityLeft =
        newCheckPacks[index].quantity + Number(quantityInput);
      newCheckPacks[index].quantityInput = 0;
      const dataForm = {};
      dataForm[`${newCheckPacks[index].id}+${dropId}`] = 0;
      dataForm[`barcode_${newCheckPacks[index].id}+${dropId}`] = "";
      formSelectPack.setFieldsValue(dataForm);
    }
    setCheckPacks(newCheckPacks);
  };

  const checkPack = (id) => {
    return checkPacks.find((p) => p.id === id);
  };

  // NEW: Helper to get pickup location name from pickupId
  const getPickupLocationName = (pId) => {
    if (!pickups) return "No location selected";
    const pickup = pickups.find((p) => p.id === pId);
    return (
      pickup?.location_name ||
      pickup?.address?.location_name ||
      pickup?.address?.address ||
      "No location selected"
    );
  };

  // NEW: Helper to get dropoff info by ID for tooltip display
  const getDropoffInfo = (dropoffId) => {
    if (!dropoffs) return { number: "Unknown", locationName: "", address: "" };
    const dropoff = dropoffs.find((d) => d.id === dropoffId);
    if (!dropoff) return { number: "Unknown", locationName: "", address: "" };

    // Find the index to display as "Dropoff #1", "Dropoff #2", etc.
    const dropoffIndex = dropoffs.findIndex((d) => d.id === dropoffId);
    const dropoffNumber = dropoffIndex >= 0 ? dropoffIndex + 1 : "Unknown";
    const locationName =
      dropoff?.location_name || dropoff?.address?.location_name || "No location selected";
    const address = dropoff?.address?.address || "";

    return { number: dropoffNumber, locationName, address };
  };

  // NEW: Group checkPacks by pickupId for rendering
  const packagesByPickup = {};
  checkPacks.forEach((pack) => {
    const pId = pack.pickupId || "unknown";
    if (!packagesByPickup[pId]) {
      packagesByPickup[pId] = [];
    }
    packagesByPickup[pId].push(pack);
  });

  const onFinish = (values) => {
    const fields = {
      packs: checkPacks.map((pack) => {
        const formBarcode = values[`barcode_${pack.id}+${dropId}`];
        const finalBarcode = formBarcode || pack.barcode || "";
        console.log(
          `SelectPack onFinish - Pack ${pack.id}: formBarcode="${formBarcode}", packBarcode="${pack.barcode}", finalBarcode="${finalBarcode}"`
        );
        return {
          id: pack.id,
          value: Number(pack.quantityInput),
          barcode: finalBarcode
        };
      }),
      dropId
    };
    onOk && onOk(fields);
  };
  console.log("packsForm", packsForm);
  // NEW: Handle "Add All" button - selects all packages with max available quantity
  const handleAddAll = () => {
    const newCheckPacks = [...checkPacks];
    const dataForm = {};

    newCheckPacks.forEach((pack) => {
      if (!pack.isDisabled) {
        pack.isCheck = true;
        pack.quantityInput = pack.quantity; // Max available
        dataForm[`${pack.id}+${dropId}`] = pack.quantity;
      }
    });

    setCheckPacks(newCheckPacks);
    formSelectPack.setFieldsValue(dataForm);
  };

  // NEW: Handle "Add All from Pickup" button
  const handleAddAllFromPickup = (pickupId) => {
    const newCheckPacks = [...checkPacks];
    const dataForm = {};

    newCheckPacks.forEach((pack) => {
      if (pack.pickupId === pickupId && !pack.isDisabled) {
        pack.isCheck = true;
        pack.quantityInput = pack.quantity; // Max available
        dataForm[`${pack.id}+${dropId}`] = pack.quantity;
      }
    });

    setCheckPacks(newCheckPacks);
    formSelectPack.setFieldsValue(dataForm);
  };

  return (
    <div className="SelectPack">
      {/* NEW: Modal Header with Add All button */}
      {dropoffAddress && (
        <div className="select-pack-header">
          <div className="dropoff-info-container">
            <div className="dropoff-info">
              <p className="dropoff-location">
                {dropoffs?.find((d) => d.id === dropId)?.location_name ||
                  "Dropoff Location"}
              </p>
              <p className="dropoff-address">{dropoffAddress}</p>
            </div>
          </div>
          <Tooltip title="Adds every package available from every pickup">
            <button type="button" className="btn-add-all" onClick={handleAddAll}>
              Add All available
            </button>
          </Tooltip>
        </div>
      )}

      <Form
        scrollToFirstError={true}
        layout="vertical"
        form={formSelectPack}
        onValuesChange={onChangeInput}
        hideRequiredMark
        colon={false}
        onFinish={onFinish}
      >
        {/* NEW: Render packages grouped by pickup location */}
        {Object.keys(packagesByPickup).map((pId, pickupIndex) => {
          const pickupLocationName = getPickupLocationName(pId);
          const packagesForPickup = packagesByPickup[pId];

          // Get the pickup to find its address
          const pickup = pickups?.find((p) => p.id === pId);
          const pickupAddress = pickup?.address?.address;

          return (
            <div key={pId} className="pickup-group">
              {/* Pickup group header with Add All from Pickup button */}
              <div className="pickup-group-header">
                <div className="pickup-info">
                  <p className="pickup-label">
                    📍 Pickup #{pickupIndex + 1} - {pickupLocationName}
                  </p>
                  {pickupAddress && <p className="pickup-address">{pickupAddress}</p>}
                </div>
                <Tooltip title="Adds available packages from this pickup">
                  <button
                    type="button"
                    className="btn-add-all-pickup"
                    onClick={() => handleAddAllFromPickup(pId)}
                  >
                    Add All from Pickup
                  </button>
                </Tooltip>
              </div>

              {/* Packages for this pickup, grouped by type */}
              {Object.keys(packsForm).map((typeKey) => {
                // Filter packages of this type for this pickup
                const packagesOfTypeForPickup = packsForm[typeKey].filter(
                  (pack) => pack.pickupId === pId
                );

                if (packagesOfTypeForPickup.length === 0) return null;

                return (
                  <div className="packItem" key={`${pId}-${typeKey}`}>
                    <div className="package-type-header">📦 {typeKey} Package</div>
                    <div className="package-table">
                      {packagesOfTypeForPickup.map((pack, index) => {
                        const checkPackData = checkPack(pack.id);
                        const allocQty = checkPackData.allocatedDropoffs
                          ? checkPackData.allocatedDropoffs.reduce(
                              (sum, a) => sum + a.quantity,
                              0
                            )
                          : 0;
                        const remaining =
                          checkPackData.totalQuantity -
                          allocQty -
                          checkPackData.quantityInput;

                        return (
                          <div
                            className={`package-row ${
                              checkPackData.isCheck ? "selected" : ""
                            } ${checkPackData.isDisabled ? "disabled" : ""}`}
                            key={pack.id}
                          >
                            {/* Checkbox */}
                            <div className="col-checkbox">
                              <Checkbox
                                onChange={(e) => onChangeCheck(e, pack.id)}
                                checked={checkPackData.isCheck}
                                disabled={checkPackData.isDisabled}
                              />
                            </div>

                            {/* Package Name */}
                            <div className="col-name">
                              {index + 1}. {pack.name}
                            </div>

                            {/* Quantity Input */}
                            <div className="col-quantity-input">
                              {checkPackData.isCheck ? (
                                <Form.Item
                                  rules={[
                                    { required: true, message: "Required" },
                                    () => ({
                                      validator(_, value) {
                                        if (!value) {
                                          return Promise.reject();
                                        }
                                        if (Number(value) < 1) {
                                          return Promise.reject("Must be at least 1");
                                        }
                                        if (Number(value) > checkPackData.quantity) {
                                          return Promise.reject(
                                            `Cannot exceed available quantity (${checkPackData.quantity})`
                                          );
                                        }
                                        return Promise.resolve();
                                      }
                                    })
                                  ]}
                                  name={`${pack.id}+${dropId}`}
                                  noStyle
                                >
                                  <InputNumber
                                    min={1}
                                    max={checkPackData.quantity}
                                    placeholder="0"
                                    className="quantity-input-field"
                                    controls={true}
                                  />
                                </Form.Item>
                              ) : (
                                <span>-</span>
                              )}
                            </div>

                            {/* Total Quantity */}
                            <div className="col-total">
                              of {checkPackData.totalQuantity}
                            </div>

                            {/* Allocation Status */}
                            <div className="col-allocation">
                              {checkPackData.allocatedDropoffs &&
                              checkPackData.allocatedDropoffs.length > 0 ? (
                                <div className="allocation-status">
                                  <span className="allocation-text">
                                    {allocQty} on other dropoffs
                                  </span>
                                  <Tooltip
                                    title={
                                      <div className="dropoff-tooltip">
                                        <div className="tooltip-label">
                                          Already allocated:
                                        </div>
                                        {checkPackData.allocatedDropoffs.map((alloc) => {
                                          const dropoffInfo = getDropoffInfo(
                                            alloc.dropoffId
                                          );
                                          return (
                                            <div
                                              key={alloc.dropoffId}
                                              className="tooltip-item"
                                            >
                                              • Dropoff #{dropoffInfo.number} (
                                              {dropoffInfo.locationName}):{" "}
                                              {alloc.quantity} qty
                                            </div>
                                          );
                                        })}
                                      </div>
                                    }
                                  >
                                    <span className="info-icon">ⓘ</span>
                                  </Tooltip>
                                </div>
                              ) : (
                                <span className="allocation-text available">
                                  All available
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </Form>
    </div>
  );
};

export default SelectPack;
